import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { ReactComponent as EditIcon } from 'src/Images/icons/common/EditIcon.svg';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import { ReactComponent as AdditionalDetailsIcon } from 'src/Images/icons/common/additionalDetailsIcon.svg';
import getColorCode from 'src/utils/GetThemeColor';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import {
  getUnitOfMeasurements,
  getVehicleTrackingDetail,
} from 'src/redux/Actions/VehicleTracking';
import { updateVehicleTrackingDetails } from 'src/redux/Actions/VehicleTrackingOutward';
import { ACTIONS } from 'src/utils/roles/actions';
import { usePermissions } from 'src/utils/hooks/usePermissions';

function OutwardVehicleTrackingAdditionalDetails() {
  const dispatch = useDispatch();
  const { vehicleTrackingNumber, workflowStageId } = useParams();

  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer.userData.tenant?.theme;
  const organisationId = authReducer.userData.organisations?.id;

  const vehicleTrackingDataReducer = useSelector(
    (state) => state?.VehicleTrackingReducer,
  );
  const { vehicleTrackingDetails, unitOfMeasurements } =
    vehicleTrackingDataReducer;
  const [isDriverDetailsInEditMode, setIsDriverDetailsInEditMode] =
    useState(false);
  const [isDocumentDetailsInEditMode, setIsDocumentDetailsInEditMode] =
    useState(false);
  const [isVehicleDetailsInEditMode, setIsVehicleDetailsInEditMode] =
    useState(false);
  const [isDriverPPEInEditMode, setIsDriverPPEInEditMode] = useState(false);

  const [unitSearchTerm, setUnitSearchTerm] = useState('');
  const { hasPermission } = usePermissions();
  const hasEditVehicleTrackingDetailsPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.EDIT,
  );

  const conditionChoices = [
    { id: 1, name: 'Average' },
    { id: 2, name: 'Good' },
    { id: 3, name: 'Excellent' },
  ];

  const fuelTypeChoices = [
    { id: '1', name: 'Petrol' },
    { id: '2', name: 'Diesel' },
    { id: '3', name: 'CNG' },
    { id: '4', name: 'LPG' },
    { id: '5', name: 'Electric' },
    { id: '6', name: 'Hybrid' },
  ];

  const statusChoices = [
    { id: '1', name: 'Active' },
    { id: '2', name: 'Inactive' },
  ];

  const ownershipChoices = [
    { id: '1', name: 'Owned' },
    { id: '2', name: 'Leased' },
    { id: '3', name: 'Rented' },
  ];

  const [formData, setFormData] = useState({
    driverName: '',
    driverMobileNumber: '',
    drivingLicenseNumber: '',
    driverLicenseValidity: '',
    remarks: '',
    pucNumber: '',
    pucValidityStartDate: '',
    pucValidUpto: '',
    registrationCertificate: '',
    insuranceNumber: '',
    insuranceStartDate: '',
    insuranceEndDate: '',
    fitnessCertificateNumber: '',
    fitnessCertificateStartDate: '',
    fitnessCertificateEndDate: '',
    capacity: '',
    unitOfMeasurementId: null,
    tankCondition: '',
    informationPanel: '',
    lidCondition: '',
    tyreCondition: '',
    tremCard: false,
    reflectors: false,
    safetyHelmet: false,
    safetyShoes: false,
    safetyHandgloves: false,
    safetyGoggles: false,
    model: '',
    brand: '',
    fuelType: '',
    registrationDate: '',
    registrationEndDate: '',
    purchaseDate: '',
    address: '',
    vehicleCategory: '',
    vehicleClassDescription: '',
    vehicleTypeCode: '',
    vehicleClassCode: '',
    bodyType: '',
    grossVehicleWeight: '',
    vin: '',
    colour: '',
    status: '',
    ownership: '',
    pucValidityEndDate: '',
  });

  useEffect(() => {
    dispatch(getUnitOfMeasurements(organisationId, unitSearchTerm));
  }, [dispatch, organisationId, unitSearchTerm]);

  useEffect(() => {
    if (!vehicleTrackingDetails) return;
    setFormData({
      driverName: vehicleTrackingDetails.driverName || '',
      driverMobileNumber: vehicleTrackingDetails.driverMobileNumber || '',
      drivingLicenseNumber: vehicleTrackingDetails.drivingLicenseNumber || '',
      driverLicenseValidity: vehicleTrackingDetails.driverLicenseValidity || '',
      remarks: vehicleTrackingDetails.remarks || '',
      pucNumber: vehicleTrackingDetails.pucNumber || '',
      pucValidityStartDate: vehicleTrackingDetails.pucValidityStartDate || '',
      pucValidUpto: vehicleTrackingDetails.pucValidUpto || '',
      registrationCertificate:
        vehicleTrackingDetails.registrationCertificate || '',
      insuranceNumber: vehicleTrackingDetails.insuranceNumber || '',
      insuranceStartDate: vehicleTrackingDetails.insuranceStartDate || '',
      insuranceEndDate: vehicleTrackingDetails.insuranceEndDate || '',
      fitnessCertificateNumber:
        vehicleTrackingDetails.fitnessCertificateNumber || '',
      fitnessCertificateStartDate:
        vehicleTrackingDetails.fitnessCertificateStartDate || '',
      fitnessCertificateEndDate:
        vehicleTrackingDetails.fitnessCertificateEndDate || '',
      capacity: vehicleTrackingDetails.capacity || '',
      unitOfMeasurementId:
        vehicleTrackingDetails.capacityUnitOfMeasurement?.id || null,
      tankCondition: vehicleTrackingDetails.tankCondition?.id
        ? String(vehicleTrackingDetails.tankCondition.id)
        : '',
      informationPanel: vehicleTrackingDetails.informationPanel?.id
        ? String(vehicleTrackingDetails.informationPanel.id)
        : '',
      lidCondition: vehicleTrackingDetails.lidCondition?.id
        ? String(vehicleTrackingDetails.lidCondition.id)
        : '',
      tyreCondition: vehicleTrackingDetails.tyreCondition?.id
        ? String(vehicleTrackingDetails.tyreCondition.id)
        : '',
      tremCard: vehicleTrackingDetails.tremCard || false,
      reflectors: vehicleTrackingDetails.reflectors || false,
      safetyHelmet: vehicleTrackingDetails.safetyHelmet || false,
      safetyShoes: vehicleTrackingDetails.safetyShoes || false,
      safetyHandgloves: vehicleTrackingDetails.safetyHandgloves || false,
      safetyGoggles: vehicleTrackingDetails.safetyGoggles || false,
      model: vehicleTrackingDetails.model || '',
      brand: vehicleTrackingDetails.brand || '',
      fuelType: vehicleTrackingDetails.fuelType?.id
        ? String(vehicleTrackingDetails.fuelType.id)
        : '',
      registrationDate: vehicleTrackingDetails.registrationDate || '',
      registrationEndDate: vehicleTrackingDetails.registrationEndDate || '',
      purchaseDate: vehicleTrackingDetails.purchaseDate || '',
      address: vehicleTrackingDetails.address || '',
      vehicleCategory: vehicleTrackingDetails.vehicleCategory || '',
      vehicleClassDescription:
        vehicleTrackingDetails.vehicleClassDescription || '',
      vehicleTypeCode: vehicleTrackingDetails.vehicleTypeCode || '',
      vehicleClassCode: vehicleTrackingDetails.vehicleClassCode || '',
      bodyType: vehicleTrackingDetails.bodyType || '',
      grossVehicleWeight: vehicleTrackingDetails.grossVehicleWeight || '',
      vin: vehicleTrackingDetails.vin || '',
      colour: vehicleTrackingDetails.colour || '',
      status: vehicleTrackingDetails.status?.id
        ? String(vehicleTrackingDetails.status.id)
        : '',
      ownership: vehicleTrackingDetails.ownership?.id
        ? String(vehicleTrackingDetails.ownership.id)
        : '',
      pucValidityEndDate: vehicleTrackingDetails.pucValidityEndDate || '',
    });
  }, [vehicleTrackingDetails]);

  const unitOfMeasurementOptions = unitOfMeasurements?.map((unitObj) => ({
    label: unitObj.name,
    value: unitObj.id,
  }));

  const handleChange = ({ target: { name, value, type, checked } }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = (closeEditMode) => {
    dispatch(
      updateVehicleTrackingDetails({
        tenant_organisation_id: organisationId,
        vehicle_tracking_number: vehicleTrackingNumber,
        driver_name: formData.driverName || null,
        driver_mobile_number: formData.driverMobileNumber || null,
        driving_license_number: formData.drivingLicenseNumber || null,
        driver_license_validity: formData.driverLicenseValidity || null,
        remarks: formData.remarks || null,
        puc_number: formData.pucNumber || null,
        puc_validity_start_date: formData.pucValidityStartDate || null,
        puc_valid_upto: formData.pucValidUpto || null,
        registration_certificate: formData.registrationCertificate || null,
        insurance_number: formData.insuranceNumber || null,
        insurance_start_date: formData.insuranceStartDate || null,
        insurance_end_date: formData.insuranceEndDate || null,
        fitness_certificate_number: formData.fitnessCertificateNumber || null,
        fitness_certificate_start_date:
          formData.fitnessCertificateStartDate || null,
        fitness_certificate_end_date:
          formData.fitnessCertificateEndDate || null,
        capacity: formData.capacity || null,
        capacity_unit_of_measurement_id: formData.unitOfMeasurementId || null,
        tank_condition: formData.tankCondition || null,
        information_panel: formData.informationPanel || null,
        lid_condition: formData.lidCondition || null,
        tyre_condition: formData.tyreCondition || null,
        trem_card: formData.tremCard,
        reflectors: formData.reflectors,
        safety_helmet: formData.safetyHelmet,
        safety_shoes: formData.safetyShoes,
        safety_handgloves: formData.safetyHandgloves,
        safety_goggles: formData.safetyGoggles,
        model: formData.model || null,
        brand: formData.brand || null,
        fuel_type: formData.fuelType || null,
        registration_date: formData.registrationDate || null,
        registration_end_date: formData.registrationEndDate || null,
        purchase_date: formData.purchaseDate || null,
        address: formData.address || null,
        vehicle_category: formData.vehicleCategory || null,
        vehicle_class_description: formData.vehicleClassDescription || null,
        vehicle_type_code: formData.vehicleTypeCode || null,
        vehicle_class_code: formData.vehicleClassCode || null,
        body_type: formData.bodyType || null,
        gross_vehicle_weight: formData.grossVehicleWeight || null,
        vin: formData.vin || null,
        colour: formData.colour || null,
        status: formData.status || null,
        ownership: formData.ownership || null,
        puc_validity_end_date: formData.pucValidityEndDate || null,
      }),
    ).then(() => {
      closeEditMode(false);
      dispatch(
        getVehicleTrackingDetail(
          vehicleTrackingNumber,
          organisationId,
          workflowStageId,
        ),
      );
    });
  };

  return (
    <>
      {/* Driver Details */}
      <div className="div-flex-row div-space-between padding-bottom-top div-align-center padding-left-right border-bottom">
        <div className="text-md text-medium-bold">Driver Details</div>
        <div className="div-flex-row div-align-center cg-10">
          {hasEditVehicleTrackingDetailsPermission &&
            (!isDriverDetailsInEditMode ? (
              <EditIcon
                className="icon-size-xl cursor-pointer"
                color="#9F9F9F"
                onClick={() => setIsDriverDetailsInEditMode(true)}
              />
            ) : (
              <button
                type="button"
                className={`common-btn-filled theme-background-${themeColor}`}
                onClick={() => handleSave(setIsDriverDetailsInEditMode)}
              >
                Submit
              </button>
            ))}
          <AdditionalDetailsIcon
            className="icon-size-xxl cursor-pointer margin-right-10"
            color="#9F9F9F"
          />
        </div>
      </div>
      <Row className="vertical-padding-20 mx-0">
        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Driver Name</span>
            {!isDriverDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.driverName || '-/-'}
              </span>
            ) : (
              <input
                name="driverName"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.driverName}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Mobile Number</span>
            {!isDriverDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.driverMobileNumber || '-/-'}
              </span>
            ) : (
              <input
                name="driverMobileNumber"
                className="margin-top-5 form-input-section"
                type="text"
                maxLength={10}
                inputMode="numeric"
                value={formData.driverMobileNumber}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">License Number</span>
            {!isDriverDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.drivingLicenseNumber || '-/-'}
              </span>
            ) : (
              <input
                name="drivingLicenseNumber"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.drivingLicenseNumber}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">License Validity</span>
            {!isDriverDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.driverLicenseValidity || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="driverLicenseValidity"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.driverLicenseValidity}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Document Details */}
      <div className="div-flex-row div-space-between padding-bottom-top div-align-center padding-left-right border-bottom border-top">
        <div className="text-md text-medium-bold">Document Details</div>
        <div className="div-flex-row div-align-center cg-10">
          {hasEditVehicleTrackingDetailsPermission &&
            (!isDocumentDetailsInEditMode ? (
              <EditIcon
                className="icon-size-xl cursor-pointer"
                color="#9F9F9F"
                onClick={() => setIsDocumentDetailsInEditMode(true)}
              />
            ) : (
              <button
                type="button"
                className={`common-btn-filled theme-background-${themeColor}`}
                onClick={() => handleSave(setIsDocumentDetailsInEditMode)}
              >
                Submit
              </button>
            ))}
          <AdditionalDetailsIcon
            className="icon-size-xxl cursor-pointer margin-right-10"
            color="#9F9F9F"
          />
        </div>
      </div>
      <Row className="vertical-padding-20 mx-0">
        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">PUC Number</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.pucNumber || '-/-'}
              </span>
            ) : (
              <input
                name="pucNumber"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.pucNumber}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">PUC Start Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.pucValidityStartDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="pucValidityStartDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.pucValidityStartDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">PUC End Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.pucValidUpto || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="pucValidUpto"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.pucValidUpto}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Registration Certificate</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.registrationCertificate || '-/-'}
              </span>
            ) : (
              <input
                name="registrationCertificate"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.registrationCertificate}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Insurance Number</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.insuranceNumber || '-/-'}
              </span>
            ) : (
              <input
                name="insuranceNumber"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.insuranceNumber}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Insurance Start Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.insuranceStartDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="insuranceStartDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.insuranceStartDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Insurance End Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.insuranceEndDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="insuranceEndDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.insuranceEndDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Fitness Certificate Number</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.fitnessCertificateNumber || '-/-'}
              </span>
            ) : (
              <input
                name="fitnessCertificateNumber"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.fitnessCertificateNumber}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Fitness Certificate Start Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.fitnessCertificateStartDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="fitnessCertificateStartDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.fitnessCertificateStartDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Fitness Certificate End Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.fitnessCertificateEndDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="fitnessCertificateEndDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.fitnessCertificateEndDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>
        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Model</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.model || '-/-'}
              </span>
            ) : (
              <input
                name="model"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.model}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Brand</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.brand || '-/-'}
              </span>
            ) : (
              <input
                name="brand"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.brand}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Fuel Type</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.fuelType?.name || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <select
                  name="fuelType"
                  onChange={handleChange}
                  className="margin-top-5 form-input-section input-icon-remove"
                  value={formData.fuelType}
                >
                  <option value="">Select a value</option>
                  {fuelTypeChoices.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </select>
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Registration Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.registrationDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="registrationDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.registrationDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Registration End Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.registrationEndDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="registrationEndDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.registrationEndDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Purchase Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.purchaseDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="purchaseDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Address</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.address || '-/-'}
              </span>
            ) : (
              <input
                name="address"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.address}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Vehicle Category</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.vehicleCategory || '-/-'}
              </span>
            ) : (
              <input
                name="vehicleCategory"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.vehicleCategory}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Vehicle Class Description</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.vehicleClassDescription || '-/-'}
              </span>
            ) : (
              <input
                name="vehicleClassDescription"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.vehicleClassDescription}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Vehicle Type Code</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.vehicleTypeCode || '-/-'}
              </span>
            ) : (
              <input
                name="vehicleTypeCode"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.vehicleTypeCode}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Vehicle Class Code</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.vehicleClassCode || '-/-'}
              </span>
            ) : (
              <input
                name="vehicleClassCode"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.vehicleClassCode}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Body Type</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.bodyType || '-/-'}
              </span>
            ) : (
              <input
                name="bodyType"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.bodyType}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Gross Vehicle Weight</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.grossVehicleWeight || '-/-'}
              </span>
            ) : (
              <input
                name="grossVehicleWeight"
                className="margin-top-5 form-input-section"
                type="number"
                value={formData.grossVehicleWeight}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">VIN</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.vin || '-/-'}
              </span>
            ) : (
              <input
                name="vin"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.vin}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Colour</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.colour || '-/-'}
              </span>
            ) : (
              <input
                name="colour"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.colour}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Status</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.status?.name || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <select
                  name="status"
                  onChange={handleChange}
                  className="margin-top-5 form-input-section input-icon-remove"
                  value={formData.status}
                >
                  <option value="">Select a value</option>
                  {statusChoices.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </select>
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Ownership</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.ownership?.name || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <select
                  name="ownership"
                  onChange={handleChange}
                  className="margin-top-5 form-input-section input-icon-remove"
                  value={formData.ownership}
                >
                  <option value="">Select a value</option>
                  {ownershipChoices.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </select>
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">PUC Validity End Date</span>
            {!isDocumentDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.pucValidityEndDate || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <input
                  name="pucValidityEndDate"
                  className="margin-top-5 form-input-section input-icon-date"
                  type="date"
                  value={formData.pucValidityEndDate}
                  onChange={handleChange}
                />
                <span className="form-input-section-icon">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Vehicle Details */}
      <div className="div-flex-row div-space-between padding-bottom-top div-align-center padding-left-right border-bottom border-top">
        <div className="text-md text-medium-bold">Vehicle Details</div>
        <div className="div-flex-row div-align-center cg-10">
          {hasEditVehicleTrackingDetailsPermission &&
            (!isVehicleDetailsInEditMode ? (
              <EditIcon
                className="icon-size-xl cursor-pointer"
                color="#9F9F9F"
                onClick={() => setIsVehicleDetailsInEditMode(true)}
              />
            ) : (
              <button
                type="button"
                className={`common-btn-filled theme-background-${themeColor}`}
                onClick={() => handleSave(setIsVehicleDetailsInEditMode)}
              >
                Submit
              </button>
            ))}
          <AdditionalDetailsIcon
            className="icon-size-xxl cursor-pointer margin-right-10"
            color="#9F9F9F"
          />
        </div>
      </div>
      <Row className="vertical-padding-20 mx-0">
        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Capacity</span>
            {!isVehicleDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.capacity
                  ? `${vehicleTrackingDetails.capacity}${
                      vehicleTrackingDetails?.capacityUnitOfMeasurement?.name
                        ? ` ${vehicleTrackingDetails.capacityUnitOfMeasurement.name}`
                        : ''
                    }`
                  : '-/-'}
              </span>
            ) : (
              <input
                name="capacity"
                className="margin-top-5 form-input-section"
                type="number"
                value={formData.capacity}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Unit Of Measurement</span>
            {!isVehicleDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.capacityUnitOfMeasurement?.name ||
                  '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <CustomMultiSelect
                  name="unitOfMeasurementId"
                  isMulti={false}
                  className="multi-select-input-section margin-top-5"
                  onChange={(selectedOption) => {
                    setFormData((prev) => ({
                      ...prev,
                      unitOfMeasurementId: selectedOption?.value || null,
                    }));
                  }}
                  onInputChange={(inputValue, { action }) => {
                    if (action === 'input-change') {
                      setUnitSearchTerm(inputValue);
                    }
                  }}
                  options={unitOfMeasurementOptions}
                  classNamePrefix="react-select"
                  placeholder="Select Unit"
                  value={unitOfMeasurementOptions?.find(
                    (option) => option.value === formData.unitOfMeasurementId,
                  )}
                />
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Tank Condition</span>
            {!isVehicleDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.tankCondition?.name || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <select
                  name="tankCondition"
                  onChange={handleChange}
                  className="margin-top-5 form-input-section input-icon-remove"
                  value={formData.tankCondition}
                >
                  <option value="">Select a value</option>
                  {conditionChoices.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </select>
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Information Panel</span>
            {!isVehicleDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.informationPanel?.name || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <select
                  name="informationPanel"
                  onChange={handleChange}
                  className="margin-top-5 form-input-section input-icon-remove"
                  value={formData.informationPanel}
                >
                  <option value="">Select a value</option>
                  {conditionChoices.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </select>
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Lid Condition</span>
            {!isVehicleDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.lidCondition?.name || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <select
                  name="lidCondition"
                  onChange={handleChange}
                  className="margin-top-5 form-input-section input-icon-remove"
                  value={formData.lidCondition}
                >
                  <option value="">Select a value</option>
                  {conditionChoices.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </select>
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Tyre Condition</span>
            {!isVehicleDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.tyreCondition?.name || '-/-'}
              </span>
            ) : (
              <div className="input-wrapper">
                <select
                  name="tyreCondition"
                  onChange={handleChange}
                  className="margin-top-5 form-input-section input-icon-remove"
                  value={formData.tyreCondition}
                >
                  <option value="">Select a value</option>
                  {conditionChoices.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </select>
                <span className="form-input-section-icon">
                  <DropdownIcon
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                </span>
              </div>
            )}
          </div>
        </Col>
        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Remarks</span>
            {!isVehicleDetailsInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.remarks || '-/-'}
              </span>
            ) : (
              <input
                name="remarks"
                className="margin-top-5 form-input-section"
                type="text"
                value={formData.remarks}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>
      </Row>

      {/* Requirements */}
      <div className="div-flex-row div-space-between padding-bottom-top div-align-center padding-left-right border-bottom border-top">
        <div className="text-md text-medium-bold">Driver PPE</div>
        <div className="div-flex-row div-align-center cg-10">
          {hasEditVehicleTrackingDetailsPermission &&
            (!isDriverPPEInEditMode ? (
              <EditIcon
                className="icon-size-xl cursor-pointer"
                color="#9F9F9F"
                onClick={() => setIsDriverPPEInEditMode(true)}
              />
            ) : (
              <button
                type="button"
                className={`common-btn-filled theme-background-${themeColor}`}
                onClick={() => handleSave(setIsDriverPPEInEditMode)}
              >
                Submit
              </button>
            ))}
          <AdditionalDetailsIcon
            className="icon-size-xxl cursor-pointer margin-right-10"
            color="#9F9F9F"
          />
        </div>
      </div>
      <Row className="vertical-padding-20 mx-0">
        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">TREM Card</span>
            {!isDriverPPEInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.tremCard ? 'Yes' : 'No'}
              </span>
            ) : (
              <input
                name="tremCard"
                type="checkbox"
                checked={formData.tremCard}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Reflectors</span>
            {!isDriverPPEInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.reflectors ? 'Yes' : 'No'}
              </span>
            ) : (
              <input
                name="reflectors"
                type="checkbox"
                checked={formData.reflectors}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Safety Helmet</span>
            {!isDriverPPEInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.safetyHelmet ? 'Yes' : 'No'}
              </span>
            ) : (
              <input
                name="safetyHelmet"
                type="checkbox"
                checked={formData.safetyHelmet}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Safety Shoes</span>
            {!isDriverPPEInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.safetyShoes ? 'Yes' : 'No'}
              </span>
            ) : (
              <input
                name="safetyShoes"
                type="checkbox"
                checked={formData.safetyShoes}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Safety Handgloves</span>
            {!isDriverPPEInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.safetyHandgloves ? 'Yes' : 'No'}
              </span>
            ) : (
              <input
                name="safetyHandgloves"
                type="checkbox"
                checked={formData.safetyHandgloves}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>

        <Col
          xxl={3}
          xl={3}
          lg={3}
          md={6}
          xs={6}
          className="form-input-container"
        >
          <div className="div-flex-column">
            <span className="text-sm">Safety Goggles</span>
            {!isDriverPPEInEditMode ? (
              <span className="text-sm text-medium-bold">
                {vehicleTrackingDetails?.safetyGoggles ? 'Yes' : 'No'}
              </span>
            ) : (
              <input
                name="safetyGoggles"
                type="checkbox"
                checked={formData.safetyGoggles}
                onChange={handleChange}
              />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
}

export default OutwardVehicleTrackingAdditionalDetails;
